// SplineHero: renders the static KostajaRobot first, then lazy-loads the
// Spline scene viewer on idle. Mobile and reduced-motion users keep the
// static visual. Spline never blocks navigation, text, CTAs, or layout.
//
// Per requirements: one Spline instance per page max.
import React, { useEffect, useRef, useState } from "react";
import KostajaRobot from "@/components/KostajaRobot";

const SPLINE_URL = "https://prod.spline.design/Sw4Xz6fKMOusy3xJ/scene.splinecode";

const isMobile = () =>
  typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const SplineHero = ({ className = "" }) => {
  const [load, setLoad] = useState(false);
  const [ready, setReady] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (isMobile() || prefersReducedMotion()) return;

    const trigger = () => setLoad(true);
    const ric =
      typeof window !== "undefined" && window.requestIdleCallback
        ? window.requestIdleCallback
        : (cb) => setTimeout(cb, 1500);
    const idleId = ric(trigger, { timeout: 2500 });

    return () => {
      if (typeof window !== "undefined" && window.cancelIdleCallback && typeof idleId === "number") {
        window.cancelIdleCallback(idleId);
      } else {
        clearTimeout(idleId);
      }
    };
    // Mount-only. isMobile and prefersReducedMotion are stable module-level fns.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!load) return;
    const el = document.createElement("spline-viewer");
    el.setAttribute("url", SPLINE_URL);
    el.setAttribute("loading-anim-type", "none");
    el.style.width = "100%";
    el.style.height = "100%";
    el.style.display = "block";
    el.addEventListener("load", () => setReady(true));
    // safety: mark ready after 3s even if no event
    const safety = setTimeout(() => setReady(true), 3000);

    if (containerRef.current) {
      containerRef.current.appendChild(el);
    }
    return () => {
      clearTimeout(safety);
      if (containerRef.current && el.parentNode === containerRef.current) {
        containerRef.current.removeChild(el);
      }
    };
  }, [load]);

  return (
    <div
      data-testid="spline-hero"
      className={`relative w-full h-full ${className}`}
      aria-hidden="true"
    >
      <div
        className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 ${
          ready ? "opacity-0" : "opacity-100"
        }`}
      >
        <KostajaRobot className="h-full max-h-[560px] w-auto" />
      </div>
      <div
        ref={containerRef}
        data-testid="spline-viewer-container"
        className={`absolute inset-0 transition-opacity duration-500 ${
          ready ? "opacity-100" : "opacity-0"
        }`}
      />
    </div>
  );
};

export default SplineHero;
